// See documentation: https://github.com/BCoulange/cornis-documentation/wiki/Authentication-Yvon%E2%80%90E
// for more information on the authentication mechanism
// -------------------

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

const initialState = {
  userMetadataLoadedInStore: false, // true if the user metadatas have been retrieved
  user_id: "",
  email: "",
  officeId: "",
  role: "",
  projects: [],
};

//  handle user auth for action or access
const isUserAllowed = (state, needed_role, otherInfos = {}) => {
  // admin is allowed no matter what
  if (state.auth.role === "admin")
    return true

  if (!otherInfos.projectId)
    return false
  // get project
  const project = state.auth.projects.find(p => p.id === otherInfos.projectId);

  // if either project is not found or user is not found
  if (!project || !state.auth.user_id) {
    return false;
  }
  
  // For global_reader: reader access everywhere, higher permissions only if in project
  if (state.auth.role === "global_reader") {
    // For reader access: always allowed
    if (needed_role === "reader") {
      return true;
    }
    // For higher permissions: must be in project userIds
    if (!project.userIds.includes(state.auth.user_id)) {
      return false;
    }
  } else {
    // Non-global_reader users need to be in userIds
    if (!project.userIds.includes(state.auth.user_id)) {
      return false;
    }
  }

  //  else does one of the user offices has access to action or page ?

  // get user offices (global_reader can be in offices for higher permissions)
  const os = project.offices.filter((o) => o.userIds.includes(state.auth.user_id))

  // handles missions access through offices
  if (otherInfos.missionId) {
    return os.some((o) => o.missionIds.some((id) => id === otherInfos.missionId))
  }

  // wich roles allowed ?
  const roles = os.flatMap(o => o.roles);

  switch (needed_role) {
    case "projectAdmin":
      return project.adminIds.includes(state.auth.user_id)
    case "contributor":
      return roles.includes("contributor")
    case "reader":
      // global_reader has same rights as reader, contributor is allowed same and more rights
      return state.auth.role === "global_reader" || roles.some(role => role === "reader" || role === "contributor")
    case "production":
      return roles.some(role => role === "production")
    default:
      return false
  }
}

export const authSlice = createSlice({
  name: "auth",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setUserMetadata: (state, { payload: newState }) => {
      return {
        ...state,
        ...newState,
        userMetadataLoadedInStore: true
      };
    },
    logout: (state) => {
      // Send an event to logout on all the tabs
      console.log("Broadcasting logout")
      const events_channel = new BroadcastChannel(window.orthophoto_bc_channel + "_events");
      events_channel.postMessage({type: "logout"});
      // Remove user metadata
      return {
        userMetadataLoadedInStore: false
      };
    },
  },
});

export const { setUserMetadata, logout } = authSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectUserMetadataLoadedInStore = (state) => state.auth.userMetadataLoadedInStore;
export const selectCurrentUserId = (state) => state.auth.user_id;
export const selectCurrentUserEmail = (state) => state.auth.email;
export const selectCurrentOffice = (state) => state.auth.officeId;

// Simple check if user can see help buttons (SITES or CORNIS email)
export const selectCanSeeHelpButtons = (state) => 
  state.auth.email?.includes('@sites.fr') || state.auth.email?.includes('@cornis.fr');

export const selectCanAccess = (state) => {
  return (pageName, otherInfos = {}) => {
    // get project if exists
    const project = state.auth.projects.find(p => p.id === otherInfos.projectId);
    // If the user is not authenticated, no access possible
    if (state.auth.userMetadataLoadedInStore !== true) return false;
    // Otherwise, access depends on the page
      
    switch (pageName) {
      // Admin pages (needs a user.role == admin):
      case "UserPage":
      case "OfficesPage":
      case "MissionsPage":
      case "AccessTrackingPage":
        return isUserAllowed(state, "admin");
      case "ProjectEditPage":
        return isUserAllowed(state, "production", otherInfos);
      case "AnnotationTablePage":
      case "accessMission":
        return isUserAllowed(state, "reader", otherInfos);
      case "PotreeViewer":
        // a mission must of theproject contains the potreeLayer
        const missionContainsPotreeLayer = project.missions.some((el) => {
          return el.potreeLayers.some((p) => {
            return p.id === otherInfos.potreeLayerId;
          });
        });
        if (missionContainsPotreeLayer) {
          // if potreeLayer is there is user allowed to access project ?
          return isUserAllowed(state, "reader", otherInfos);
        } else {
          return false
        }
      case "Ortho": {
        const { inspectionId, viewId } = otherInfos;
        const missionContainsInspectionAndView = project.missions.some((el) => {
          return el.inspections.some((i) => {
            if (i.id !== inspectionId) return false;
            return i.views.some((v) => v.id === viewId);
          });
        });
        return missionContainsInspectionAndView ? isUserAllowed(state, "reader", otherInfos) : false;
      }
      case "PhotoViewer": {
        const { inspectionId, viewId } = otherInfos;
        if (!project || !project.missions) return isUserAllowed(state, "reader", otherInfos);
        const missionContainsInspectionAndView = project.missions.some((el) => {
          return (el.inspections ?? []).some((i) => {
            if (i.id !== inspectionId) return false;
            const hasView = i.views?.some((v) => v.id === viewId) ?? false;
            return i.views?.length ? hasView : true; // views not in auth payload => allow by inspection only
          });
        });
        return missionContainsInspectionAndView ? isUserAllowed(state, "reader", otherInfos) : false;
      }
      default:
        return false;
    }
  }
};

export const selectCanDo = (state) => {
  return (action, otherInfos= {}) => {
    switch (action) {
      case "reupload":
        return isUserAllowed(state, "admin");
      case "createProject":
        return isUserAllowed(state, "admin");
      case "createMission":
      case "createInspection":
      case "administrate": // user can administrate at least one project
        const projects = state.auth.projects
        const adminInatleastOne = projects.some((p) => {
          return isUserAllowed(state, "projectAdmin", {projectId: p.id});
        })
        return adminInatleastOne
      case "createAnnotation":
        return isUserAllowed(state, "contributor", otherInfos);
      case "exportAnnotations":
        if (state.auth.role === "admin") return true;
        if (!otherInfos.projectId) return false;
        const exportProject = state.auth.projects.find(p => p.id === otherInfos.projectId);
        if (!exportProject || !state.auth.user_id) return false;
        return exportProject.userIds.includes(state.auth.user_id);
      case "createMission":
      case "createInspection":
        return isUserAllowed(state, "production", otherInfos);
      case "manage3DLayers":
        return (
          isUserAllowed(state, "production", otherInfos) ||
          isUserAllowed(state, "projectAdmin", otherInfos)
        );
      default:
        return false;
    }
  };
};

export default authSlice.reducer;